import React, { useState } from "react";
import { ChevronRight, ChevronDown, Menu, X } from "lucide-react";
import ContainsDuplicateVisualizer from "./components/arrays/contains_duplicates";
import ProductOfArrayExceptSelf from "./components/arrays/product_of_array_except_self";
import MinimumInSortedRotated from "./components/arrays/minimum_in_sorted_rotated_array";
import SearchInSortedRotatedArray from "./components/arrays/search_in_rotated_sorted_array";
import ThreeSum from "./components/arrays/three_sum";
import MaximumProductSubarray from "./components/arrays/maximum_product_subarray";
import TwoSum from "./components/arrays/two_sum";
import MaximumSubarray from "./components/arrays/maximum_subarray";
import BestTimeToBuySellStocks from "./components/arrays/best_time_to_buy_sell_stocks";
import ContainerWithMostWater from "./components/arrays/container_with_most_water";
import MissingNumberVisualizer from "./components/binary/missing_number";
import SumOfTwoIntegersVisualizer from "./components/binary/sum_of_2_integers";
import ReverseLinkedListVisualizer from "./components/linked_list/reverse_linked_list";
import MergeTwoSortedListsVisualizer from "./components/linked_list/merge_two_sorted_list";
import DetectCycleVisualizer from "./components/linked_list/detect_cycle";
import MergeKSortedListsVisualizer from "./components/linked_list/merge_k_sorted_list";
import RemoveNthNodeVisualizer from "./components/linked_list/remove_nth_node_from_list";
import ReorderListVisualizer from "./components/linked_list/reorder_list";
import InsertIntervalVisualizer from "./components/interval/insert_interval";
import LongestPalindromicSubstringVisualizer from "./components/string/longest_palindromic_substring_no_dp";
import MergeIntervalsVisualizer from "./components/interval/merge_intervals";
import FindRepeatingAndMissingVisualizer from "./components/interval/find_missing_and_repeating";
import NonOverlappingIntervalsVisualizer from "./components/interval/non_overlapping_intervals";
import MeetingRoomsVisualizer from "./components/interval/meeting_rooms";
import SetMatrixZeroesVisualizer from "./components/matrix/set_matrix_zero";
import SpiralMatrixVisualizer from "./components/matrix/spiral_matrix_traversal";
import RotateMatrixVisualizer from "./components/matrix/rotate_matrix";
import WordSearchVisualizer from "./components/matrix/word_search";
import LongestSubstringVisualizer from "./components/string/length_of_longetst_substring_without_repeating_character";
import LongestRepeatingCharacterReplacementVisualizer from "./components/string/longest_repeating_character_replacement";
import MinimumWindowSubstringVisualizer from "./components/string/minimum_window_substring";
import ValidAnagramVisualizer from "./components/string/valid_anagram";
import GroupAnagramsVisualizer from "./components/string/group_anagrams";
import BalancedParenthesesVisualizer from "./components/string/check_for_balance_paranthesis";
import PalindromeNumberVisualizer from "./components/string/check_palindrome";
import PalindromicSubstringsVisualizer from "./components/string/palindromic_substrings";
import TopKFrequentElementsVisualizer from "./components/heap/top_k_frequent_element";
import FindMedianVisualizer from "./components/heap/find_median_from_data_stream";

// Main App Component
export default function Blind75Visualizer() {
  const [selectedProblem, setSelectedProblem] = useState("two-sum");
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    arrays: true,
    binary: false,
    dynamicProgramming: false,
    graph: false,
    interval: false,
    linkedList: false,
    matrix: false,
    string: false,
    tree: false,
    heap: false,
  });

  const disabledTopics = new Set([
    "tree",
    "binary",
    "dynamicProgramming",
    "graph",
  ]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const problemCategories = {
    arrays: {
      name: "Arrays",
      problems: {
        "two-sum": { name: "Two Sum", component: TwoSum, difficulty: "Easy" },
        stock: {
          name: "Best Time to Buy and Sell Stock",
          component: BestTimeToBuySellStocks,
          difficulty: "Easy",
        },
        container: {
          name: "Container With Most Water",
          component: ContainerWithMostWater,
          difficulty: "Medium",
        },
        duplicate: {
          name: "Contains Duplicate",
          component: ContainsDuplicateVisualizer,
          difficulty: "Easy",
        },
        product: {
          name: "Product of Array Except Self",
          component: ProductOfArrayExceptSelf,
          difficulty: "Medium",
        },
        "max-subarray": {
          name: "Maximum Subarray",
          component: MaximumSubarray,
          difficulty: "Medium",
        },
        "max-product": {
          name: "Maximum Product Subarray",
          component: MaximumProductSubarray,
          difficulty: "Medium",
        },
        "min-rotated": {
          name: "Find Minimum in Rotated Sorted Array",
          component: MinimumInSortedRotated,
          difficulty: "Medium",
        },
        "search-rotated": {
          name: "Search in Rotated Sorted Array",
          component: SearchInSortedRotatedArray,
          difficulty: "Medium",
        },
        "3sum": { name: "3 Sum", component: ThreeSum, difficulty: "Medium" },
      },
    },
    matrix: {
      name: "Matrix",
      problems: {
        "set-matrix-zeroes": {
          name: "Set Matrix Zeroes",
          component: SetMatrixZeroesVisualizer,
          difficulty: "Medium",
        },
        "spiral-matrix": {
          name: "Spiral Matrix",
          component: SpiralMatrixVisualizer,
          difficulty: "Medium",
        },
        "rotate-image": {
          name: "Rotate Image",
          component: RotateMatrixVisualizer,
          difficulty: "Medium",
        },
        "word-search": {
          name: "Word Search",
          component: WordSearchVisualizer,
          difficulty: "Medium",
        },
      },
    },
    interval: {
      name: "Interval",
      problems: {
        "insert-interval": {
          name: "Insert Interval",
          component: InsertIntervalVisualizer,
          difficulty: "Medium",
        },
        "merge-intervals": {
          name: "Merge Intervals",
          component: MergeIntervalsVisualizer,
          difficulty: "Medium",
        },
        "non-overlapping": {
          name: "Non-overlapping Intervals",
          component: NonOverlappingIntervalsVisualizer,
          difficulty: "Medium",
        },
        "repeat-missing": {
          name: "Repeat and Missing Number",
          component: FindRepeatingAndMissingVisualizer,
          difficulty: "Hard",
        },
        "meeting-rooms": {
          name: "Meeting Rooms",
          component: MeetingRoomsVisualizer,
          difficulty: "Medium",
        },
        "meeting-rooms-ii": {
          name: "Meeting Rooms II",
          component: null,
          difficulty: "Medium",
        },
      },
    },
    linkedList: {
      name: "Linked List",
      problems: {
        "reverse-linked-list": {
          name: "Reverse Linked List",
          component: ReverseLinkedListVisualizer,
          difficulty: "Easy",
        },
        "detect-cycle": {
          name: "Linked List Cycle",
          component: DetectCycleVisualizer,
          difficulty: "Easy",
        },
        "merge-two-lists": {
          name: "Merge Two Sorted Lists",
          component: MergeTwoSortedListsVisualizer,
          difficulty: "Easy",
        },
        "merge-k-lists": {
          name: "Merge k Sorted Lists",
          component: MergeKSortedListsVisualizer,
          difficulty: "Hard",
        },
        "remove-nth-node": {
          name: "Remove Nth Node From End",
          component: RemoveNthNodeVisualizer,
          difficulty: "Medium",
        },
        "reorder-list": {
          name: "Reorder List",
          component: ReorderListVisualizer,
          difficulty: "Medium",
        },
      },
    },
    string: {
      name: "String",
      problems: {
        "longest-substring": {
          name: "Longest Substring Without Repeating",
          component: LongestSubstringVisualizer,
          difficulty: "Medium",
        },
        "longest-repeating": {
          name: "Longest Repeating Character Replacement",
          component: LongestRepeatingCharacterReplacementVisualizer,
          difficulty: "Medium",
        },
        "minimum-window": {
          name: "Minimum Window Substring",
          component: MinimumWindowSubstringVisualizer,
          difficulty: "Hard",
        },
        "valid-anagram": {
          name: "Valid Anagram",
          component: ValidAnagramVisualizer,
          difficulty: "Easy",
        },
        "group-anagrams": {
          name: "Group Anagrams",
          component: GroupAnagramsVisualizer,
          difficulty: "Medium",
        },
        "valid-parentheses": {
          name: "Valid Parentheses",
          component: BalancedParenthesesVisualizer,
          difficulty: "Easy",
        },
        "valid-palindrome": {
          name: "Valid Palindrome",
          component: PalindromeNumberVisualizer,
          difficulty: "Easy",
        },
        "longest-palindromic": {
          name: "Longest Palindromic Substring",
          component: LongestPalindromicSubstringVisualizer,
          difficulty: "Medium",
        },
        "palindromic-substrings": {
          name: "Palindromic Substrings",
          component: PalindromicSubstringsVisualizer,
          difficulty: "Medium",
        },
      },
    },
    heap: {
      name: "Heap",
      problems: {
        "merge-k-sorted": {
          name: "Merge k Sorted Lists",
          component: MergeKSortedListsVisualizer,
          difficulty: "Hard",
        },
        "top-k-frequent": {
          name: "Top K Frequent Elements",
          component: TopKFrequentElementsVisualizer,
          difficulty: "Medium",
        },
        "find-median": {
          name: "Find Median from Data Stream",
          component: FindMedianVisualizer,
          difficulty: "Hard",
        },
      },
    },
    tree: {
      name: "Tree",
      problems: {
        "max-depth": {
          name: "Maximum Depth of Binary Tree",
          component: null,
          difficulty: "Easy",
        },
        "same-tree": { name: "Same Tree", component: null, difficulty: "Easy" },
        "invert-tree": {
          name: "Invert Binary Tree",
          component: null,
          difficulty: "Easy",
        },
        "binary-tree-max-path": {
          name: "Binary Tree Maximum Path Sum",
          component: null,
          difficulty: "Hard",
        },
        "level-order": {
          name: "Binary Tree Level Order Traversal",
          component: null,
          difficulty: "Medium",
        },
        "serialize-deserialize": {
          name: "Serialize and Deserialize Binary Tree",
          component: null,
          difficulty: "Hard",
        },
        subtree: {
          name: "Subtree of Another Tree",
          component: null,
          difficulty: "Easy",
        },
        "construct-from-preorder": {
          name: "Construct Binary Tree from Preorder and Inorder",
          component: null,
          difficulty: "Medium",
        },
        "validate-bst": {
          name: "Validate Binary Search Tree",
          component: null,
          difficulty: "Medium",
        },
        "kth-smallest": {
          name: "Kth Smallest Element in a BST",
          component: null,
          difficulty: "Medium",
        },
        lca: {
          name: "Lowest Common Ancestor of BST",
          component: null,
          difficulty: "Medium",
        },
        trie: { name: "Implement Trie", component: null, difficulty: "Medium" },
        "add-search-word": {
          name: "Design Add and Search Words Data Structure",
          component: null,
          difficulty: "Medium",
        },
        "word-search-ii": {
          name: "Word Search II",
          component: null,
          difficulty: "Hard",
        },
      },
    },
    binary: {
      name: "Binary",
      problems: {
        "sum-of-two": {
          name: "Sum of Two Integers",
          component: SumOfTwoIntegersVisualizer,
          difficulty: "Easy",
        },
        "number-of-1-bits": {
          name: "Number of 1 Bits",
          component: null,
          difficulty: "Easy",
        },
        "counting-bits": {
          name: "Counting Bits",
          component: null,
          difficulty: "Easy",
        },
        "missing-number": {
          name: "Find missing number in an array",
          component: MissingNumberVisualizer,
          difficulty: "Easy",
        },
        "reverse-bits": {
          name: "Reverse Bits",
          component: null,
          difficulty: "Easy",
        },
      },
    },
    graph: {
      name: "Graph",
      problems: {
        "clone-graph": {
          name: "Clone Graph",
          component: null,
          difficulty: "Hard",
        },
        "course-schedule": {
          name: "Course Schedule - I",
          component: null,
          difficulty: "Hard",
        },
        "pacific-atlantic": {
          name: "Pacific Atlantic Water Flow",
          component: null,
          difficulty: "Medium",
        },
        "number-of-islands": {
          name: "Number of islands",
          component: null,
          difficulty: "Hard",
        },
        "longest-consecutive": {
          name: "Longest Consecutive Sequence",
          component: null,
          difficulty: "Medium",
        },
        "alien-dictionary": {
          name: "Alien Dictionary",
          component: null,
          difficulty: "Hard",
        },
        "graph-valid-tree": {
          name: "Graph Valid Tree",
          component: null,
          difficulty: "Medium",
        },
        "connected-components": {
          name: "Connected Components",
          component: null,
          difficulty: "Hard",
        },
      },
    },
    dynamicProgramming: {
      name: "Dynamic Programming",
      problems: {
        "climbing-stairs": {
          name: "Climbing Stairs",
          component: null,
          difficulty: "Medium",
        },
        "coin-change": {
          name: "Coin Change",
          component: null,
          difficulty: "Hard",
        },
        "longest-increasing": {
          name: "Longest Increasing Subsequence",
          component: null,
          difficulty: "Medium",
        },
        "longest-common": {
          name: "Longest Common Subsequence",
          component: null,
          difficulty: "Medium",
        },
        "word-break": {
          name: "Word Break",
          component: null,
          difficulty: "Medium",
        },
        "combination-sum": {
          name: "Combination Sum",
          component: null,
          difficulty: "Medium",
        },
        "max-sum-non-adjacent": {
          name: "Maximum sum of non-adjacent elements",
          component: null,
          difficulty: "Medium",
        },
        "house-robber": {
          name: "House Robber",
          component: null,
          difficulty: "Medium",
        },
        "decode-ways": {
          name: "Decode Ways",
          component: null,
          difficulty: "Medium",
        },
        "unique-paths": {
          name: "Grid Unique Paths",
          component: null,
          difficulty: "Medium",
        },
        "jump-game": {
          name: "Jump Game",
          component: null,
          difficulty: "Medium",
        },
      },
    },
  };

  let CurrentVisualizer = null;
  for (const category of Object.values(problemCategories)) {
    if (category.problems[selectedProblem]) {
      CurrentVisualizer = category.problems[selectedProblem].component;
      break;
    }
  }

  if (!CurrentVisualizer) {
    CurrentVisualizer = () => (
      <div className="p-8 text-center text-gray-500 flex flex-col justify-center items-center h-full">
        <p className="text-2xl font-semibold mb-4">Visualizer Coming Soon!</p>
        <p>
          This problem's interactive visualizer is under construction. Please
          select another problem from the list.
        </p>
      </div>
    );
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "Easy":
        return "text-green-600";
      case "Medium":
        return "text-yellow-600";
      case "Hard":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <div className="relative min-h-screen bg-gray-50 lg:flex">
      <div className="sticky top-0 z-10 bg-white shadow-sm lg:hidden flex justify-between items-center p-4">
        <h1 className="text-xl font-bold text-gray-800">Blind 75 Visualizer</h1>
        <button onClick={() => setIsNavVisible(true)} className="p-2 -mr-2">
          <Menu size={24} />
        </button>
      </div>

      {isNavVisible && (
        <div
          onClick={() => setIsNavVisible(false)}
          className="fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity lg:hidden"
          aria-hidden="true"
        ></div>
      )}

      <div
        className={`fixed inset-y-0 left-0 z-30 w-72 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:static lg:w-1/5 lg:min-w-[280px] lg:translate-x-0 lg:shadow-none lg:border-r lg:border-gray-200 lg:h-screen lg:sticky lg:top-0 ${
          isNavVisible ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-full overflow-y-auto">
          <div className="p-4 flex justify-between items-center sticky top-0 bg-white z-10 border-b lg:border-none">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-800">
              Blind 75
            </h1>
            <button
              onClick={() => setIsNavVisible(false)}
              className="p-2 -mr-2 lg:hidden"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-4 space-y-2">
            {Object.entries(problemCategories).map(([key, category]) => {
              const isTopicDisabled = disabledTopics.has(key);
              return (
                <div key={key}>
                  <button
                    onClick={() => toggleSection(key)}
                    className="flex items-center justify-between w-full px-3 py-2 text-left text-gray-600 hover:bg-gray-100 hover:text-gray-900 rounded-md transition-colors"
                  >
                    <span
                      className={`font-semibold ${
                        isTopicDisabled ? "text-gray-400" : ""
                      }`}
                    >
                      {category.name}
                    </span>
                    <span
                      className={`${isTopicDisabled ? "text-gray-400" : ""}`}
                    >
                      {expandedSections[key] ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                    </span>
                  </button>

                  {expandedSections[key] && (
                    <div className="mt-2 pl-2 space-y-1 border-l-2 border-gray-200">
                      {Object.entries(category.problems).map(
                        ([problemKey, problem]) => (
                          <button
                            key={problemKey}
                            onClick={() => {
                              if (!isTopicDisabled) {
                                setSelectedProblem(problemKey);
                                setIsNavVisible(false);
                              }
                            }}
                            className={`w-full px-3 py-2 text-left text-sm rounded-md transition-all duration-150 ${
                              isTopicDisabled
                                ? "text-gray-400 cursor-not-allowed"
                                : selectedProblem === problemKey
                                ? "bg-indigo-600 text-white font-medium shadow-sm"
                                : "text-gray-700 hover:bg-indigo-100 hover:text-indigo-800"
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span>
                                {problem.name}
                                {!problem.component && (
                                  <span className="text-xs ml-1 opacity-60">
                                    (Soon)
                                  </span>
                                )}
                              </span>
                              <span
                                className={`text-xs font-semibold ${
                                  isTopicDisabled
                                    ? "text-gray-400"
                                    : selectedProblem === problemKey
                                    ? "text-indigo-100"
                                    : getDifficultyColor(problem.difficulty)
                                }`}
                              >
                                {problem.difficulty}
                              </span>
                            </div>
                          </button>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FIXED: The classes h-screen and overflow-y-auto are now prefixed with lg: 
          so they only apply on large screens. This fixes the mobile layout and scrolling. */}
      <main className="w-full lg:flex-1 lg:h-screen lg:overflow-y-auto">
        <CurrentVisualizer />
      </main>
    </div>
  );
}
